import { z } from 'zod';
/**
 * 0.22.0: reading platform-api's answers forward compatibly.
 *
 * Product backends parse platform-api's answers with the copy of this package they vendored, which can be several
 * versions older than the platform-api that answers. A closed enum in an answer then breaks every product the day
 * platform-api adds a value: contracts 0.18.0 gave owners and administrators the tickets.* permissions, and every
 * product on an older copy refused their access check ("We could not check your access").
 *
 * The helpers below make a field of an ANSWER drop (or fall back from) a value this copy does not know, instead of
 * failing. They are codecs, not transforms, for two reasons:
 * - platform-api serializes every answer with z.encode (fastify-type-provider-zod), which refuses transforms. Encoding
 *   validates the strict schema, so platform-api itself still never sends a value outside its own version.
 * - The output type stays the strict one (an OrgRole[] stays an OrgRole[]), so no code that reads these answers changes.
 *
 * Only a value that is a string this copy does not know is dropped. Anything else that does not fit (a number, a
 * missing field, a list where a string belongs) still fails: that is a broken answer, not a newer one.
 *
 * Never use these in a schema platform-api validates a REQUEST with: what it accepts stays strict.
 */
/** True when value is a string outside known: a value a later version added, which this copy cannot read. */
export function isUnknownValue(value, known) {
    return typeof value === 'string' && !known.includes(value);
}
/**
 * A list of enum values in an answer: values this copy does not know are dropped, the known ones keep their order.
 * Its output type is the enum's array type, exactly as z.array(values) would give.
 */
export function knownValues(values) {
    const known = values.options;
    return z.codec(z.array(z.string()), z.array(values), {
        decode: (raw) => raw.filter((value) => known.includes(value)),
        // Encoding (platform-api's own serializer) has already checked every value against the strict list.
        encode: (list) => [...list],
    });
}
/**
 * A list of objects in an answer where an entry this copy cannot read is dropped: isUnknownEntry says which (an entry
 * naming a product, a kind of context or a role this copy does not know, judged with isUnknownValue). Every other
 * entry is parsed with entry as usual, so a malformed one still fails.
 */
export function knownEntries(entry, isUnknownEntry) {
    return z.codec(z.array(z.unknown()), z.array(entry), {
        decode: (raw) => raw.filter((item) => !isUnknownEntry(item)),
        encode: (list) => [...list],
    });
}
/**
 * One value in an answer that reads as fallback when isUnknown says this copy cannot read it (an unknown staff role
 * reads as no staff role, for example). Choose a fallback that grants nothing. Every other value is parsed with schema.
 */
export function knownOr(schema, isUnknown, fallback) {
    // The input is optional so that an absent field reaches schema, which decides whether absence is allowed.
    return z.codec(z.unknown().optional(), schema, {
        decode: (raw) => (isUnknown(raw) ? fallback : raw),
        encode: (value) => value,
    });
}
/** The value at key of raw when raw is a plain object (an entry of an answer), otherwise undefined. */
export function fieldOf(raw, key) {
    return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw[key]
        : undefined;
}
//# sourceMappingURL=forward-compatible.js.map